import type { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/core';
import type {
  ErrorSchema,
  FormValidation,
  RJSFSchema,
  RJSFValidationError,
  WidgetProps,
} from '@rjsf/utils';
import type { FC } from 'react';
import { forwardRef, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconTileProps } from '../icon-tile/icon-tile';
import type { HeadingLevel } from '../mantine-rich-text-editor';
import templates from './templates';
import type { SchemaFormData } from './utils';
import { clearEmptyUriValues, parseFormData } from './utils';
import { useCustomValidation } from './validation';
import widgets from './widgets';

export interface SchemaFormOptions {
  showFields?: string[];
  acceptFileTypes?: string[];
  submitButtonText?: string;
  hideSubmitButton?: boolean;
  defaultCurrency?: string;
  hasDividerBetweenFields?: boolean;
  clearableFields?: boolean;
  hideOptionalLabels?: boolean;
  dateFormat?: string;
  onImageUpload?: (file: File) => Promise<string>;
  richTextHeadings?: HeadingLevel[];
  iframeEnabled?: boolean;
}

export type SchemaFormFieldOptions = Record<
  string,
  {
    acceptFileTypes?: string[];
    maxFileSize?: number;
    iconTiles?: Partial<IconTileProps>[];
    'ui:widget'?: FC<WidgetProps>; // Used by RSJF for custom widgets
    minDate?: string;
    maxDate?: string;
    isSearchable?: boolean;
    searchLabel?: string;
    showObjectFields?: string[];
    // Current only used by CurrencyWidget
    infoMessage?: string | ((value: string) => string | undefined);
    warningMessage?: string | ((value: string) => string | undefined);
    // Image options
    croppableImage?: boolean;
    imageAspectRatio?: number;
    imagePreviewWidth?: string;
    imagePreviewHeight?: string;
    // Input options
    hideMaxLength?: boolean;
  }
>;

export interface SchemaFormProps {
  id?: string;
  schema: RJSFSchema;
  onSubmit: (data: SchemaFormData) => void;
  onChange?: (data: SchemaFormData) => void;
  onError?: (errors: RJSFValidationError[]) => void;
  onValidate?: (data: SchemaFormData, errors: FormValidation) => void;
  formData?: SchemaFormData;
  formOptions?: SchemaFormOptions;
  fieldOptions?: SchemaFormFieldOptions;
  liveValidate?: boolean;
  focusOnFirstError?: boolean;
  disabled?: boolean;
  extraErrors?: ErrorSchema;
}

const SchemaForm = forwardRef(
  (
    {
      id,
      schema,
      onSubmit,
      onChange,
      onError,
      onValidate,
      formData,
      formOptions = {},
      fieldOptions = {},
      liveValidate = false,
      focusOnFirstError = true,
      disabled = false,
      extraErrors,
    }: SchemaFormProps,
    ref: React.Ref<Form>
  ) => {
    const { t } = useTranslation();
    const { submitButtonText, hideSubmitButton } = formOptions;
    const { customValidate, transformErrors, validator } = useCustomValidation(
      schema,
      onValidate
    );

    const uiSchema = useMemo(
      () => ({
        'ui:submitButtonOptions': {
          submitText:
            submitButtonText ?? t('words.submit', { defaultValue: 'Submit' }),
          norender: hideSubmitButton,
        },
        ...fieldOptions,
      }),
      [submitButtonText, hideSubmitButton, fieldOptions, t]
    );

    const handleChange = useCallback(
      ({ formData }: IChangeEvent) => onChange?.(formData as SchemaFormData),
      [onChange]
    );

    const handleSubmit = useCallback(
      ({ formData }: IChangeEvent) => onSubmit(formData as SchemaFormData),
      [onSubmit]
    );

    const handleFormError = useCallback(
      (errors: RJSFValidationError[]) => {
        for (const error of errors) {
          if (error.stack.startsWith('schema is invalid')) {
            console.warn(error.stack);
          }
        }
        if (focusOnFirstError) {
          const selector = `#root_${errors[0].property}`;
          const input = document.querySelector(selector) as HTMLInputElement;
          input?.focus();
        }
        onError?.(errors);
      },
      [focusOnFirstError, onError]
    );

    const parsedFormData = useMemo(
      () => clearEmptyUriValues(parseFormData(formData), schema),
      [formData, schema]
    );

    return (
      <Form
        id={id}
        ref={ref}
        schema={schema}
        uiSchema={uiSchema}
        templates={templates}
        widgets={widgets}
        validator={validator}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onError={handleFormError}
        showErrorList={false}
        customValidate={customValidate}
        transformErrors={transformErrors}
        formData={parsedFormData}
        formContext={formOptions}
        liveValidate={liveValidate}
        disabled={disabled}
        extraErrors={extraErrors}
      />
    );
  }
);

SchemaForm.displayName = 'SchemaForm';

export default memo(SchemaForm);
